import { ButtonGroup, ButtonText, Col, DateSelector, Dialog, DialogCardBody, DialogCardFooter, DialogCardHead, DialogCardHeadIcon, DialogCardHeadTitle, Grid, Icon, Row, Typography } from "@visa/vds";
import { DateHelper } from "_helpers/DateHelper";
import MCSReadonlyField from "components/MCSReadonlyField";
import React, { useContext, useEffect, useState } from "react";
import isValid from 'date-fns/isValid';
import { trackPromise } from "react-promise-tracker";
import { certificationPackageListService } from "services/certification.packageList";
import { useAuthContextUser } from "context/AuthContext";
import { GlobalAlertContext } from "context/GlobalAlertContext";
import MCSToolTip from "components/MCSToolTip";

const AdminExtendSubscription = ({ fetchData, search, filters, pagination, open, setOpen,
    subscription, companyName, futureSubscription, haveFutureSubscription, sorter }) => {

    const REQUIRED_END_DATE = "Please select an End Date before proceeding."
    const [minEndDate, setMinEndDate] = useState(null);
    const [toConfirmExtendVsts, setToConfirmExtendVsts] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorText, setErrorText] = useState(REQUIRED_END_DATE)
    const [dialogType, setDialogType] = useState("");
    const [dialogTitle, setDialogTitle] = useState("Extend VSTS Subscription");

    const user = useAuthContextUser();
    const alertContext = useContext(GlobalAlertContext);

    const [selectedEndDate, setSelectedEndDate] = useState(minEndDate);

    const handleWhenPageFirstLoads = () => {
        if (subscription && subscription.endDate != null) {
            let date = new Date(subscription.endDate)
            setMinEndDate(date);
            setSelectedEndDate(date);
        }
        setDialogType(haveFutureSubscription ? "warning" : "info")
        setDialogTitle(haveFutureSubscription ? "Warning - Future Subscription Exists" : "Extend VSTS Subscription")
    }

    const extendSubscription = () => {
        const subscriptionDto = {
            ...subscription,
            newEndDate: selectedEndDate
        }
        trackPromise(
            certificationPackageListService.AdminExtendVstsSubscription(subscriptionDto)
                .then(result => {
                    fetchData(pagination, filters, search, sorter)
                    setToConfirmExtendVsts(false);
                    setSelectedEndDate(null);
                    setShowError(false);
                    setTimeout(function () {
                        alertContext.setSuccessMessage("VSTS Subscription extended successfully.");
                    }, 2000);
                }).catch(error => {
                    alertContext.setErrorMessage("VSTS Subscription failed to be extended. Please try again.")
                })
        )
    }

    const onSelectDate = (date) => {
        if (date != null) {
            setSelectedEndDate(DateHelper.ToUTCDateTime(date));
            if (!validateDateForTLA(date)) return
            setShowError(false)
        }
    }

    const validateDateForTLA = (date) => {
        let endDatePlus14 = new Date(subscription.endDate);
        endDatePlus14.setDate(endDatePlus14.getDate() + 10);
        if (user.isVstsWorkflowAdmin && date > endDatePlus14) {
            setErrorText("VSTS Access can only be extended up to 10 days. Contact a VSS Super Admin if additional extension is needed.")
            setShowError(true)
            return false
        }
        return true;
    }

    const onClickNextBtn = () => {
        setOpen(true);
        if (showError) return
        if (selectedEndDate == null) {
            setErrorText(REQUIRED_END_DATE)
            setShowError(true)
            return
        } else if (selectedEndDate) {
            if (!validateDateForTLA(selectedEndDate)) return
        }
        setToConfirmExtendVsts(true);
    }

    useEffect(() => {
        handleWhenPageFirstLoads()
    }, [subscription, haveFutureSubscription])

    return (
        <>
            <Dialog
                aria-describedby="amend vsts subscription"
                onExit={() => { }}
                open={open}
                type={dialogType}
                titleId="amend vsts subscription"
            >
                <DialogCardHead>
                    {haveFutureSubscription && <DialogCardHeadIcon><Icon name="warning" resolution="low" /></DialogCardHeadIcon>}
                    <DialogCardHeadTitle id="dialog-title-manage-file">{dialogTitle}</DialogCardHeadTitle>
                </DialogCardHead>

                <DialogCardBody id="dialog-description-manage-file-field">
                    <Grid>
                        {haveFutureSubscription &&
                            <Row>
                                <Typography tag="h4">This BID already has a future subscription. You cannot extend an active subscription if BID has a future subscription. Please advise the client to go to the Subscription Information page to move the start date forward.</Typography>
                            </Row>
                        }
                        <Row>
                            <Col>
                                <MCSReadonlyField
                                    label="Business Name"
                                />
                            </Col>
                            <Col>{companyName}</Col>
                        </Row>
                        <Row style={{ borderBottom: (haveFutureSubscription) ? "1px solid grey" : "none" }}>
                            <Col>
                                <MCSReadonlyField
                                    label="BID"
                                />
                            </Col>
                            <Col>{subscription && subscription.bid}</Col>
                        </Row>

                        {!haveFutureSubscription &&
                            <>
                                <Row>
                                    <Col>
                                        <MCSReadonlyField
                                            label="VSTS Subscription Type"
                                        />
                                    </Col>
                                    <Col>{subscription && subscription.subscriptionName.split(' ')[0]}</Col>
                                </Row>
                                <Row style={{ borderBottom: "1px solid grey" }}>
                                    <Col>
                                        <MCSReadonlyField
                                            label="Subscription Start Date"
                                        />
                                    </Col>
                                    <Col>{subscription && DateHelper.ToFormattedDate(subscription.startDate)}</Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <MCSReadonlyField
                                            label="Subscription End Date (Current)"
                                        />
                                    </Col>
                                    <Col>{subscription && DateHelper.ToFormattedDate(subscription.endDate)}</Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <MCSReadonlyField
                                                label="Subscription End Date (New)"
                                            />
                                            <div style={{ marginLeft: "-13px", display: "flex", alignItems: "center" }}>
                                                <MCSToolTip
                                                    message="GCT Admins can only extend up to 10 days. A Super Admin is required if more than 10 days extension is required."
                                                    id="vstsWorkflowAdmin-tooltip"
                                                    iconName="info"
                                                    placement="top"
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    {!toConfirmExtendVsts &&
                                        <Col style={{ width: "0px" }}>
                                            <DateSelector
                                                id="VSTS-end-date-input"
                                                ariaLabelCalendarButtonIcon={(date) => (
                                                    isValid(new Date(date))
                                                        ? `Choose End Date, selected End date is ${date}`
                                                        : 'Choose End Date'
                                                )}
                                                name="date-picker-selected"
                                                showHelpText
                                                showErrorText
                                                errorText={errorText}
                                                helpText="Click calendar icon to select date"
                                                invalid={showError}
                                                minDate={minEndDate}
                                                startDate={minEndDate}
                                                selected={minEndDate}
                                                onSelect={date => onSelectDate(date)}
                                                formatInputValue='dd MMM yyyy'
                                            />
                                        </Col>
                                    }
                                    {toConfirmExtendVsts &&
                                        <Col>{DateHelper.ToFormattedDate(selectedEndDate)}</Col>
                                    }
                                </Row>
                            </>
                        }

                        {/* BID has future subscription */}
                        {haveFutureSubscription &&
                            <>
                                <Row>
                                    <Col>
                                        <MCSReadonlyField label="Current Subscription" />
                                        <span style={{ marginLeft: "4px" }}>{subscription && subscription.subscriptionName.split(' ')[0]}</span>
                                    </Col>
                                    <Col>
                                        <MCSReadonlyField label="Start Date" />
                                        <span style={{ marginLeft: "4px" }}>{subscription && DateHelper.ToFormattedDate(subscription.startDate)}</span>

                                    </Col>
                                    <Col>
                                        <MCSReadonlyField label="End Date" />
                                        <span style={{ marginLeft: "4px" }}>{subscription && DateHelper.ToFormattedDate(subscription.endDate)}</span>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <MCSReadonlyField label="Future Subscription" />
                                        <span style={{ marginLeft: "4px" }}>{futureSubscription && futureSubscription.subscriptionName.split(' ')[0]}</span>
                                    </Col>
                                    <Col>
                                        <MCSReadonlyField label="Start Date" />
                                        <span style={{ marginLeft: "4px" }}>{futureSubscription && DateHelper.ToFormattedDate(futureSubscription.startDate)}</span>

                                    </Col>
                                    <Col>
                                        <MCSReadonlyField label="End Date" />
                                        <span style={{ marginLeft: "4px" }}>{futureSubscription && DateHelper.ToFormattedDate(futureSubscription.endDate)}</span>
                                    </Col>
                                </Row>
                            </>
                        }
                    </Grid>
                </DialogCardBody>

                {!haveFutureSubscription &&
                    <>
                        <DialogCardFooter>
                            <ButtonGroup direction="right">
                                <ButtonText
                                    colorScheme="secondary"
                                    onClick={() => {
                                        setOpen(null);
                                        setToConfirmExtendVsts(false);
                                        setSelectedEndDate(null);
                                        setShowError(false);
                                    }}
                                >
                                    Cancel
                                </ButtonText>
                                {toConfirmExtendVsts &&
                                    <ButtonText
                                        colorScheme="primary"
                                        onClick={() => {
                                            setOpen(null);
                                            extendSubscription();
                                        }}
                                    >
                                        Confirm
                                    </ButtonText>
                                }
                                {!toConfirmExtendVsts &&
                                    <ButtonText
                                        colorScheme="primary"
                                        onClick={onClickNextBtn}
                                    >
                                        Next
                                    </ButtonText>
                                }
                            </ButtonGroup>
                        </DialogCardFooter>
                    </>
                }

                {/* extend subscription - BID has future subscription */}
                {haveFutureSubscription &&
                    <DialogCardFooter>
                        <ButtonGroup direction="right">
                            <ButtonText
                                colorScheme="primary"
                                onClick={() => {
                                    setOpen(null);
                                }}
                            >
                                OK
                            </ButtonText>
                        </ButtonGroup>
                    </DialogCardFooter>}
            </Dialog >
        </>
    )
}

export default AdminExtendSubscription;